import { useState } from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/app/MarketingNav";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Zap, Lock, Sparkles, AlertTriangle, CheckCircle2, ArrowRight, Search,
} from "lucide-react";

function SimpleMd({ text }) {
  if (!text) return null;
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {text.split("\n").map((line, i) => {
        if (line.startsWith("## ")) return <h3 key={i} className="font-semibold text-lg tracking-tight mt-4">{line.slice(3)}</h3>;
        if (line.startsWith("- ") || line.startsWith("* "))
          return <div key={i} className="flex gap-2"><span className="text-primary">›</span><span dangerouslySetInnerHTML={{ __html: line.slice(2).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} /></div>;
        if (line.trim() === "") return <div key={i} className="h-1" />;
        return <p key={i} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />;
      })}
    </div>
  );
}

export default function FreeAudit() {
  const [url, setUrl] = useState("");
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const runAudit = async (e) => {
    e?.preventDefault();
    if (!url.trim()) return;
    setAuditing(true); setAudit(null); setPlan("");
    try {
      const { data } = await api.post("/public/audit", { url });
      setAudit(data);
      window.scrollTo({ top: 280, behavior: "smooth" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    setAuditing(false);
  };

  const unlock = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setUnlocking(true);
    try {
      const { data } = await api.post("/public/leads", { url, email, score: audit?.score });
      setPlan(data.content);
      toast.success("AI plan unlocked — check it out below");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    setUnlocking(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 border border-border bg-white px-3 py-1.5 text-xs tracking-wide" data-testid="free-audit-badge">
            <Zap className="w-3.5 h-3.5" /> <span className="font-medium">Free · no signup required</span>
          </div>
          <h1 className="font-semibold tracking-tight mt-6 text-4xl md:text-6xl">
            Audit any URL.<br />
            <span className="text-primary">Get a 0–100 SEO score</span> in 10 seconds.
          </h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
            We&apos;ll crawl the page and surface real on-page issues — meta tags, headings, schema, images, performance.
            Drop your URL below.
          </p>
          <form onSubmit={runAudit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl" data-testid="free-audit-form">
            <Input
              value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://yourwebsite.com"
              className="rounded-none h-12 border-foreground/30 focus-visible:ring-foreground flex-1"
              data-testid="free-audit-url"
            />
            <Button type="submit" disabled={auditing} className="rounded-none h-12 bg-foreground hover:bg-foreground/90 px-6" data-testid="free-audit-submit">
              {auditing ? "Crawling…" : <><Search className="w-4 h-4 mr-2" /> Audit now</>}
            </Button>
          </form>
          <p className="mt-3 text-xs text-muted-foreground">No account · No credit card · Results in seconds</p>
        </div>
      </section>

      {/* RESULTS */}
      {audit && (
        <section className="border-b border-border" data-testid="free-audit-results">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12 space-y-8">
            {/* Score card */}
            <div className="border border-border bg-white p-8 flex items-center justify-between gap-6 flex-wrap">
              <div>
                <p className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">{audit.url}</p>
                <div className="flex items-baseline gap-3 mt-1">
                  <span className="font-semibold text-7xl tracking-tight">{audit.score}</span>
                  <span className="text-muted-foreground font-mono-data text-sm">/ 100</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-px border border-border bg-border font-mono-data text-xs">
                <div className="bg-white px-4 py-3"><div className="text-muted-foreground uppercase">Issues</div><div className="text-red-700 text-lg mt-1">{audit.issues?.length ?? 0}</div></div>
                <div className="bg-white px-4 py-3"><div className="text-muted-foreground uppercase">Warnings</div><div className="text-amber-700 text-lg mt-1">{audit.warnings?.length ?? 0}</div></div>
                <div className="bg-white px-4 py-3"><div className="text-muted-foreground uppercase">Passed</div><div className="text-emerald-700 text-lg mt-1">{audit.passed?.length ?? 0}</div></div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-border bg-white p-6">
                <h3 className="font-display tracking-tight text-lg flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Issues & warnings</h3>
                <ul className="mt-3 space-y-2 text-sm">
                  {audit.issues.map((it, i) => <li key={`i${i}`} className="flex gap-2"><span className="text-red-600">●</span>{it}</li>)}
                  {audit.warnings.map((it, i) => <li key={`w${i}`} className="flex gap-2"><span className="text-amber-600">●</span>{it}</li>)}
                  {audit.issues.length === 0 && audit.warnings.length === 0 && <li className="text-muted-foreground">Clean. No issues found.</li>}
                </ul>
              </div>
              <div className="border border-border bg-white p-6">
                <h3 className="font-display tracking-tight text-lg flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Passed checks</h3>
                <ul className="mt-3 space-y-2 text-sm">
                  {audit.passed.map((it, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">●</span>{it}</li>)}
                  {audit.passed.length === 0 && <li className="text-muted-foreground">No passed checks.</li>}
                </ul>
              </div>
            </div>

            {/* AI gate */}
            {!plan ? (
              <div className="border-2 border-foreground bg-white p-8 relative overflow-hidden" data-testid="ai-gate">
                <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 border border-foreground bg-background px-3 py-1.5 text-xs">
                    <Lock className="w-3.5 h-3.5" /> <span className="font-mono-data uppercase tracking-widest">Locked</span>
                  </div>
                  <h3 className="font-semibold tracking-tight text-3xl md:text-4xl mt-4">
                    Unlock the AI-powered<br />action plan.
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                    Claude Sonnet 4.5 turns these findings into a prioritized roadmap — Quick Wins, On-Page Improvements, Content Strategy, Link Building. Drop your email to view it.
                  </p>
                  <form onSubmit={unlock} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-lg" data-testid="ai-gate-form">
                    <Input value={email} onChange={(e)=>setEmail(e.target.value)} type="email" required placeholder="you@company.com"
                      className="rounded-none h-12 border-foreground/30 focus-visible:ring-foreground flex-1"
                      data-testid="ai-gate-email" />
                    <Button type="submit" disabled={unlocking} className="rounded-none h-12 bg-foreground hover:bg-foreground/90 px-6" data-testid="ai-gate-submit">
                      <Sparkles className="w-4 h-4 mr-2" /> {unlocking ? "Generating…" : "Unlock plan"}
                    </Button>
                  </form>
                  <p className="mt-3 text-xs text-muted-foreground">We&apos;ll send your full report by email. No spam — unsubscribe anytime.</p>
                </div>
              </div>
            ) : (
              <div className="border border-emerald-300 bg-emerald-50 p-8" data-testid="ai-plan">
                <h3 className="font-semibold tracking-tight text-3xl flex items-center gap-2"><Sparkles className="w-6 h-6" /> Your AI action plan</h3>
                <div className="mt-6 bg-white border border-border p-6">
                  <SimpleMd text={plan} />
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/register">
                    <Button className="rounded-none bg-foreground hover:bg-foreground/90 h-11 px-6" data-testid="post-plan-register">
                      Track this site for free <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="rounded-none h-11 px-6">Learn more</Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex items-center justify-between">
          <span className="font-semibold tracking-tight">ARLOSEO</span>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">← Back to site</Link>
        </div>
      </footer>
    </div>
  );
}
